import { useState } from 'react';
import type { ReactNode } from 'react';
import { ContextKindKeyBadge } from '@gonfalon/contexts/components/ContextKindKeyBadge';
import { getDisplayDurationFromMs } from '@gonfalon/format/getDisplayDurationFromMs';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Button, Tag, TagGroup, TagList } from '@launchpad-ui/components';

import styles from './MultiPhaseReleasePhaseDescription.module.css';

interface MultiPhaseReleaseStage {
  readonly rolloutWeight: number;
  readonly monitoringWindowMilliseconds?: number;
}

interface MultiPhaseReleasePhase {
  /** Audience description shown as the card label; falls back to "Phase {n}" when absent. */
  readonly description?: string;
  readonly clauses: ReactNode;
  readonly stages: readonly MultiPhaseReleaseStage[];
}

interface MultiPhaseReleaseMetric {
  readonly key: string;
  readonly autoRollback: boolean;
}

type MultiPhaseReleaseAdvancementMode = 'manual' | 'automatic';

export interface MultiPhaseReleasePhaseDescriptionProps {
  readonly phases: readonly MultiPhaseReleasePhase[];
  readonly originalVariationId: string;
  readonly targetVariationId: string;
  readonly rolloutContextKind: string;
  readonly metrics?: readonly MultiPhaseReleaseMetric[];
  readonly updateDefaultRuleOnCompletion?: boolean;
  readonly advancementMode?: MultiPhaseReleaseAdvancementMode;
  readonly renderVariation: (variationId: string) => ReactNode;
}

export function MultiPhaseReleasePhaseDescription({
  phases,
  originalVariationId,
  targetVariationId,
  rolloutContextKind,
  metrics,
  updateDefaultRuleOnCompletion,
  advancementMode,
  renderVariation,
}: MultiPhaseReleasePhaseDescriptionProps) {
  return (
    <div>
      <Message
        id="releases.phasedReleaseSummary.heading"
        values={{ count: phases.length, badge: (chunks) => <span className={styles.phaseCount}>{chunks}</span> }}
      />
      <div className={styles.phasesContainer}>
        {phases.map((phase, index) => (
          <PhaseCard key={index} phase={phase} phaseNumber={index + 1} />
        ))}
        <SharedAcrossAllPhases
          originalVariationId={originalVariationId}
          targetVariationId={targetVariationId}
          rolloutContextKind={rolloutContextKind}
          metrics={metrics}
          updateDefaultRuleOnCompletion={updateDefaultRuleOnCompletion}
          advancementMode={advancementMode}
          renderVariation={renderVariation}
        />
      </div>
    </div>
  );
}

function PhaseCard({ phase, phaseNumber }: { phase: MultiPhaseReleasePhase; phaseNumber: number }) {
  const description = phase.description?.trim();
  return (
    <div className={styles.phaseCard}>
      <div className={styles.phaseHeader}>
        <span className={styles.phaseLabel}>
          {description || msg('releases.phasedReleaseSummary.phaseNumber', { number: phaseNumber })}
        </span>
        <span className={styles.phaseAudience}>{phase.clauses}</span>
      </div>
      <ReleasePhaseDurationRow stages={phase.stages} />
    </div>
  );
}

function SharedAcrossAllPhases({
  originalVariationId,
  targetVariationId,
  rolloutContextKind,
  metrics,
  updateDefaultRuleOnCompletion,
  advancementMode,
  renderVariation,
}: Omit<MultiPhaseReleasePhaseDescriptionProps, 'phases'>) {
  return (
    <div className={styles.sharedSection}>
      <span className={styles.sharedHeading}>
        <Message id="releases.phasedReleaseSummary.sharedHeading" />
      </span>
      <div className={styles.container}>
        <ReleasePhaseRow label={msg('releases.phasedReleaseSummary.rolloutLabel')}>
          {renderVariation(targetVariationId)} <Message id="releases.phasedReleaseSummary.rolloutBy" />{' '}
          <ContextKindKeyBadge contextKind={{ key: rolloutContextKind }} />
        </ReleasePhaseRow>
        <ReleasePhaseRow label={msg('releases.phasedReleaseSummary.originalVariationLabel')}>
          {renderVariation(originalVariationId)}
        </ReleasePhaseRow>
        <MetricsRow metrics={metrics} />
        <AdvancementModeRow advancementMode={advancementMode} />
        <CleanupPhaseRow
          updateDefaultRuleOnCompletion={updateDefaultRuleOnCompletion}
          targetVariationId={targetVariationId}
          renderVariation={renderVariation}
        />
      </div>
    </div>
  );
}

function AdvancementModeRow({ advancementMode }: { advancementMode?: MultiPhaseReleaseAdvancementMode }) {
  return (
    <ReleasePhaseRow label={msg('releases.phasedReleaseSummary.advancementLabel')}>
      {advancementMode === 'automatic' ? (
        <Message id="releases.phasedReleaseSummary.advancementAutomatic" />
      ) : (
        <Message id="releases.phasedReleaseSummary.advancementManual" />
      )}
    </ReleasePhaseRow>
  );
}

function CleanupPhaseRow({
  updateDefaultRuleOnCompletion,
  targetVariationId,
  renderVariation,
}: {
  updateDefaultRuleOnCompletion: boolean | undefined;
  targetVariationId: string;
  renderVariation: (variationId: string) => ReactNode;
}) {
  if (updateDefaultRuleOnCompletion === undefined) {
    return null;
  }
  return (
    <ReleasePhaseRow label={msg('releases.phasedReleaseSummary.cleanupLabel')}>
      {updateDefaultRuleOnCompletion ? (
        <>
          <Message id="releases.phasedReleaseSummary.cleanupRemoveRules" /> {renderVariation(targetVariationId)}
        </>
      ) : (
        <Message id="releases.phasedReleaseSummary.cleanupRulesRemain" />
      )}
    </ReleasePhaseRow>
  );
}

function ReleasePhaseRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{children}</span>
    </div>
  );
}

/** Renders the "Rollout duration" row with a stage-count chip and an expandable per-stage table. A synthetic 100% terminal stage is appended since phased releases always end at full rollout. Returns null when the phase has no stages. */
function ReleasePhaseDurationRow({ stages }: { stages: readonly MultiPhaseReleaseStage[] }) {
  const [showStages, setShowStages] = useState(false);
  if (stages.length === 0) {
    return null;
  }
  const totalDurationMs = stages.reduce((sum, stage) => sum + (stage.monitoringWindowMilliseconds ?? 0), 0);
  const tableStages = [...stages, { rolloutWeight: 100000 }];
  const stepCount = msg('releases.phasedReleaseSummary.stepCount', { count: tableStages.length });

  return (
    <>
      <ReleasePhaseRow label={msg('releases.phasedReleaseSummary.durationLabel')}>
        {getDisplayDurationFromMs(totalDurationMs)}
        <TagGroup aria-label={msg('releases.phasedReleaseSummary.stepCountLabel')}>
          <TagList>
            <Tag size="small" textValue={stepCount}>
              {stepCount}
            </Tag>
          </TagList>
        </TagGroup>
        <Button variant="minimal" size="small" onPress={() => setShowStages((prev) => !prev)}>
          {showStages
            ? msg('releases.phasedReleaseSummary.hideDetails')
            : msg('releases.phasedReleaseSummary.showDetails')}
        </Button>
      </ReleasePhaseRow>
      {showStages && <StagesTable stages={tableStages} />}
    </>
  );
}

/** Renders the "Metrics" row with an individual-metric count and an expandable per-metric auto-rollback table. Returns null when there are no metrics. */
function MetricsRow({ metrics }: { metrics?: readonly MultiPhaseReleaseMetric[] }) {
  const [showMetrics, setShowMetrics] = useState(false);
  if (metrics === undefined || metrics.length === 0) {
    return null;
  }

  return (
    <>
      <ReleasePhaseRow label={msg('releases.phasedReleaseSummary.metricsLabel')}>
        <Message id="releases.phasedReleaseSummary.metricCount" values={{ count: metrics.length }} />
        <Button variant="minimal" size="small" onPress={() => setShowMetrics((prev) => !prev)}>
          {showMetrics
            ? msg('releases.phasedReleaseSummary.hideDetails')
            : msg('releases.phasedReleaseSummary.showDetails')}
        </Button>
      </ReleasePhaseRow>
      {showMetrics && <MetricsTable metrics={metrics} />}
    </>
  );
}

function StagesTable({ stages }: { stages: readonly MultiPhaseReleaseStage[] }) {
  return (
    <table className={styles.detailsTable}>
      <thead>
        <tr>
          <th>
            <Message id="releases.phasedReleaseSummary.stepColumn" />
          </th>
          <th>
            <Message id="releases.phasedReleaseSummary.weightColumn" />
          </th>
          <th>
            <Message id="releases.phasedReleaseSummary.durationColumn" />
          </th>
        </tr>
      </thead>
      <tbody>
        {stages.map((stage, i) => (
          <tr key={i}>
            <td>{i + 1}</td>
            <td>{stage.rolloutWeight / 1000}%</td>
            <td>
              {stage.monitoringWindowMilliseconds !== undefined
                ? getDisplayDurationFromMs(stage.monitoringWindowMilliseconds, { format: 'short' })
                : ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MetricsTable({ metrics }: { metrics: readonly MultiPhaseReleaseMetric[] }) {
  return (
    <table className={styles.detailsTable}>
      <thead>
        <tr>
          <th>
            <Message id="releases.phasedReleaseSummary.metricColumn" />
          </th>
          <th>
            <Message id="releases.phasedReleaseSummary.autoRollbackColumn" />
          </th>
        </tr>
      </thead>
      <tbody>
        {metrics.map((m) => (
          <tr key={m.key}>
            <td>{m.key}</td>
            <td>
              {m.autoRollback ? (
                <Message id="releases.phasedReleaseSummary.yes" />
              ) : (
                <Message id="releases.phasedReleaseSummary.no" />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
